import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import HeaderBar from "./components/headerBar";
import NavTabs from "./components/navTabs";
import { enrollmentDataContext } from "./layout";
import { queryClient, queryKeys } from "../../lib/queryClient";
import { fetchEnrollmentStatus } from "../../services/apiEndpoints";
import {
  Clock,
  CheckCircle2,
  Download,
  Award,
  ChevronRight,
  BookOpen,
  FileText,
  Search
} from "lucide-react";
import Button from "../../components/common/Button";
import { useToast } from "../../hooks/otherHooks/useToast";

const formatLastActive = (dateString) => {
  if (!dateString) return "Not started yet";
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "Not started yet";

    // Normalize dates to midnight to compute clean relative days differences
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const activeDate = new Date(d);
    activeDate.setHours(0, 0, 0, 0);

    const diffTime = today.getTime() - activeDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Active today";
    if (diffDays === 1) return "Active yesterday";
    if (diffDays < 7) return `Active ${diffDays} days ago`;
    return `Active on ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  } catch (e) {
    return `Active on ${dateString}`;
  }
};

const MyLearning = () => {
  const [activeTab, setActiveTab] = useState("in-progress");
  const [searchQuery, setSearchQuery] = useState("");
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const navigate = useNavigate();
  const { addToast } = useToast();

  const tabs = [
    { id: "in-progress", label: "In Progress" },
    { id: "completed", label: "Completed" }
  ];
  const { inProgressCourses, completedCourses, inProgressTracks, completedTracks } = useContext(enrollmentDataContext);
  console.log(inProgressCourses, inProgressTracks, completedCourses, completedTracks)

  const ongoing = [...(inProgressTracks || []), ...(inProgressCourses || [])];
  const completed = [...(completedTracks || []), ...(completedCourses || [])];

  const currentList = (activeTab === "in-progress" ? ongoing : completed).filter(item => {
    const title = item.course_detail?.title || item.track_detail?.title || "";
    return title.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleAccessCourse = async (item) => {
    const isCourse = !!item.course_detail;
    const id = isCourse ? item.course_detail.id : item.track_detail.id;
    const slug = isCourse ? item.course_detail.slug : item.track_detail.slug;
    const title = isCourse ? item.course_detail.title : item.track_detail.title;

    setIsCheckingStatus(true);

    try {
      const data = await queryClient.fetchQuery({
        queryKey: queryKeys.enrollments.status(
          isCourse ? { courseId: id } : { trackId: id }
        ),
        queryFn: ({ signal }) =>
          fetchEnrollmentStatus({
            courseId: isCourse ? id : undefined,
            trackId: isCourse ? undefined : id,
            signal,
          }),
      });

      if (data && data.paid) {
        // Proceed to navigate to learning page on successful payment status
        const link = isCourse
          ? `/student/dashboard/courses/${slug}/enrolled?courseId=${id}`
          : `/student/dashboard/track/${slug}/enrolled?trackId=${id}`;
        navigate(link);
      } else {
        addToast({
          message: `Access Denied: Payment is pending for "${title}". Please complete checkout.`,
          bg: "danger",
        });
      }
    } catch (error) {
      console.error("Error verifying enrollment status:", error);
      // Fallback: Let them attempt access in case of a brief network failure so they are not stranded
      const link = isCourse
        ? `/student/dashboard/courses/${slug}/enrolled?courseId=${id}`
        : `/student/dashboard/track/${slug}/enrolled?trackId=${id}`;
      navigate(link);
    } finally {
      setIsCheckingStatus(false);
    }
  };

  return (
    <section className="cht-animate-in cht-fade-in cht-duration-700">
      <HeaderBar pageHeader="My Learning">
        <NavTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabSelect={setActiveTab}
        />
      </HeaderBar>

      <main className="cht-container cht-space-y-12 cht-pb-12">

        {/* Search Bar */}
        <div className="cht-relative cht-max-w-2xl cht-mx-auto cht-group">
          <Search className="cht-absolute cht-left-4 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400 group-focus-within:cht-text-black cht-transition-colors" size={18} />
          <input
            type="text"
            placeholder="Search your library..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="cht-w-full cht-bg-white cht-border cht-border-gray-100 cht-rounded-2xl cht-py-4 cht-pl-12 cht-pr-4 cht-text-sm cht-font-medium cht-focus:border-[#3e57da] cht-outline-none cht-transition-all"
          />
        </div>

        {/* List Content */}
        <div className="cht-max-w-5xl cht-mx-auto cht-bg-white cht-rounded-[28px] cht-overflow-hidden cht-shadow-[0_10px_40px_rgba(0,0,0,0.02)] cht-border cht-border-gray-50">
          {currentList.length > 0 ? (
            <div className="cht-divide-y cht-divide-gray-50">
              {currentList.map((item, idx) => {
                const itemTitle = item.course_detail?.title || item.track_detail?.title || "Curriculum";
                return (
                  <div
                    key={idx}
                    onClick={() => handleAccessCourse(item)}
                    className="cht-p-6 hover:cht-bg-gray-50/50 cht-transition-colors cht-group cht-flex cht-flex-col md:cht-flex-row cht-items-center cht-gap-6 cht-cursor-pointer"
                  >
                    {/* Thumbnail */}
                    <div className="cht-w-full md:cht-w-40 cht-aspect-video cht-rounded-xl cht-overflow-hidden cht-bg-gray-100 cht-flex-shrink-0 cht-shadow-sm">
                      <img
                        src={(item.course_detail?.course_img_url || item.track_detail?.track_img_url) || 'https://via.placeholder.com/400x225'}
                        className="cht-w-full cht-h-full cht-object-cover group-hover:cht-scale-105 cht-transition-transform cht-duration-500"
                        alt="thumbnail"
                      />
                    </div>

                    {/* Info */}
                    <div className="cht-flex-1 cht-space-y-4 cht-w-full">
                      <div className="cht-flex cht-justify-between cht-items-start">
                        <div>
                          <p className="cht-text-[10px] cht-font-bold cht-text-[#3e57da] cht-uppercase cht-tracking-widest cht-mb-1">
                            {item.track_detail ? "Career Track" : "Standalone Course"}
                          </p>
                          <h3 className="cht-text-lg cht-font-bold cht-text-black group-hover:cht-text-[#3e57da] cht-transition-colors">
                            {itemTitle}
                          </h3>
                        </div>

                        {activeTab === "completed" && (
                          <span className="cht-flex cht-items-center cht-gap-1.5 cht-text-green-600 cht-bg-green-50 cht-px-3 cht-py-1 cht-rounded-full cht-text-[10px] cht-font-bold cht-uppercase cht-tracking-wider">
                            <CheckCircle2 size={12} /> Certificate Available
                          </span>
                        )}
                      </div>

                      {activeTab === "in-progress" ? (
                        <div className="cht-space-y-2">
                          <div className="cht-flex cht-justify-between cht-text-[10px] cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-wider">
                            <span>{item.progress_percent ?? item.progress ?? 0}% Complete</span>
                            <span>{formatLastActive(item.last_active_date)}</span>
                          </div>
                          <div className="cht-w-full cht-bg-gray-100 cht-rounded-full cht-h-1.5">
                            <div
                              className="cht-bg-[#3e57da] cht-h-1.5 cht-rounded-full cht-transition-all cht-duration-1000"
                              style={{ width: `${item.progress_percent ?? item.progress ?? 0}%` }}
                            ></div>
                          </div>
                        </div>
                      ) : (
                        <div className="cht-flex cht-items-center cht-gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="cht-text-[#3e57da]"
                            leftIcon={<Download size={14} />}
                            onClick={(e) => e.stopPropagation()}
                          >
                            Receipt
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="cht-text-[#3e57da]"
                            leftIcon={<Award size={14} />}
                            onClick={(e) => e.stopPropagation()}
                          >
                            Certificate
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Action */}
                    <div className="cht-flex-shrink-0">
                      <Button
                        variant="tab"
                        isPill
                        size="sm"
                        className="cht-p-3 cht-min-w-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAccessCourse(item);
                        }}
                      >
                        <ChevronRight size={20} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="cht-p-20 cht-flex cht-flex-col cht-items-center cht-text-center cht-space-y-6">
              <div className="cht-w-24 cht-h-24 cht-bg-gray-50 cht-rounded-full cht-flex cht-items-center cht-justify-center">
                {searchQuery ? <Search className="cht-text-gray-300 cht-w-10 cht-h-10" /> : <BookOpen className="cht-text-gray-300 cht-w-10 cht-h-10" />}
              </div>
              <div className="cht-space-y-2">
                <h3 className="cht-text-xl cht-font-bold cht-text-black">
                  {searchQuery ? "No search results" : "No learning history found"}
                </h3>
                <p className="cht-text-sm cht-text-gray-400 cht-max-w-xs">
                  {searchQuery
                    ? `We couldn't find any courses matching "${searchQuery}" in your library.`
                    : "Your progress will appear here as soon as you start your first lesson."}
                </p>
              </div>
              {searchQuery ? (
                <Button
                  variant="ghost"
                  onClick={() => setSearchQuery("")}
                  className="cht-text-[#3e57da]"
                >
                  Clear Search
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  className="cht-bg-black cht-border-none"
                  onClick={() => navigate("/student/dashboard/courses")}
                >
                  Find a Course
                </Button>
              )}
            </div>
          )}
        </div>

      </main>

      {/* Dynamic Glassmorphism Enrollment Loading Modal */}
      {isCheckingStatus && (
        <div className="cht-fixed cht-inset-0 cht-z-[3000] cht-flex cht-items-center cht-justify-center cht-bg-black/40 cht-backdrop-blur-md cht-animate-in cht-fade-in cht-duration-300">
          <div className="cht-bg-white/95 cht-backdrop-blur-2xl cht-p-8 cht-rounded-[24px] cht-shadow-2xl cht-border cht-border-white/40 cht-flex cht-flex-col cht-items-center cht-space-y-5 cht-max-w-sm cht-text-center cht-animate-in cht-zoom-in-95">
            <div className="cht-w-12 cht-h-12 cht-border-[3px] cht-border-[#3e57da] cht-border-t-transparent cht-rounded-full cht-animate-spin"></div>
            <div>
              <h4 className="cht-text-base cht-font-black cht-text-black">Verifying Credentials</h4>
              <p className="cht-text-xs cht-text-gray-500 cht-mt-1.5 cht-font-medium leading-relaxed font-semibold">Securing your student enrollment parameters. Please do not close this window.</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default MyLearning;
